import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { apiService } from '../services/apiService';
import { API_BASE_URL } from '../config/api.config';

// Define the User type to match backend response
interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

// Define the context type
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  token: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  register: (firstName: string, lastName: string, email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  error: string | null;
  setAuthLoading: (isLoading: boolean) => void;
}

// Create the context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  loading: true,
  token: null,
  login: async () => false,
  register: async () => false,
  logout: async () => { /* Default implementation, will be overridden */ },
  refreshToken: async () => false,
  error: null,
  setAuthLoading: () => { /* Default implementation, will be overridden */ }
});

// Auth Provider Props
interface AuthProviderProps {
  children: ReactNode;
}

// For dev/test only - fallback mock users when backend is not available
const MOCK_USERS = [
  {
    id: '1',
    firstName: 'Admin',
    lastName: 'User',
    email: 'ken.peluso@roiconstruction.com',
    password: 'password123',
    role: 'admin'
  },
  {
    id: '2',
    firstName: 'Regular',
    lastName: 'User',
    email: 'user@roiexteriors.com',
    password: 'password123',
    role: 'user'
  },
  {
    id: '3',
    firstName: 'Demo',
    lastName: 'User',
    email: 'dev@roiexteriors.com',
    password: 'password123',
    role: 'admin'
  }
];

// Demo credentials - always use these for test/development
const DEMO_EMAIL = 'dev@roiexteriors.com';
const DEMO_PASSWORD = 'password123';

// Token refresh constants
const TOKEN_REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes
const TOKEN_EXPIRY_BUFFER = 60 * 1000; // 1 minute before expiration

// Auth Provider Component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTimer, setRefreshTimer] = useState<NodeJS.Timeout | null>(null);

  // Parse JWT token to get expiration time
  const getTokenExpiration = useCallback((token: string): number | null => {
    try {
      // Check if token has JWT format (header.payload.signature)
      if (token.split('.').length !== 3) {
        console.warn('Token is not in JWT format, cannot parse expiration');
        return null;
      }

      // JWT tokens are in format: header.payload.signature
      const payloadBase64 = token.split('.')[1];

      // Base64 URL decode the payload
      // First replace characters for regular base64 encoding
      const base64 = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');

      // Decode with padding adjustment
      const decodedPayload = atob(base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '='));

      // Parse JSON
      const payload = JSON.parse(decodedPayload);

      // Check if token has expiration
      if (!payload.exp) return null;

      // Convert to milliseconds and return
      return payload.exp * 1000;
    } catch (e) {
      console.error('Failed to parse token:', e);
      return null;
    }
  }, []);

  // Check if token is expired or about to expire
  const isTokenExpiring = useCallback((token: string): boolean => {
    const expiration = getTokenExpiration(token);
    if (!expiration) return false;

    // Return true if token expires within buffer time
    return Date.now() + TOKEN_EXPIRY_BUFFER >= expiration;
  }, [getTokenExpiration]);

  // Refresh token function
  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      console.log('Attempting to refresh auth token');
      setError(null);

      // Check if we have a refresh token
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        console.warn('No refresh token available');
        return false;
      }

      // Attempt to refresh the token
      const response = await apiService.refreshAuthToken();

      // Update application state
      setToken(response.accessToken || response.access_token);

      // If user information is included in the response, update it
      if (response.user) {
        const roles = response.roles || (response.user.roles && response.user.roles.map((r: any) => r.name)) || [];
        
        // Remove roles from user object to avoid confusion and add role string
        const { roles: userRoles, ...userWithoutRoles } = response.user;
        const userData = {
          ...userWithoutRoles,
          role: roles.length > 0 ? roles[0] : 'user'
        };
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      }

      console.log('Token refreshed successfully');
      return true;
    } catch (err: any) {
      console.error('Token refresh error:', err);

      // Provide specific error message
      if (err.message?.includes('Network Error') || err.message?.includes('Failed to fetch')) {
        setError('Unable to connect to the authentication server for token refresh.');
      } else {
        setError(err.message || 'Token refresh failed');
      }

      return false;
    }
  }, []);

  // Setup token refresh timer
  const setupRefreshTimer = useCallback(() => {
    // For static deployments, disable token refresh completely
    if (window.location.hostname.includes('netlify.app') || 
        window.location.hostname.includes('github.io') ||
        process.env.REACT_APP_USE_MOCK_DATA === 'true') {
      console.log('Static deployment detected, disabling token refresh');
      return;
    }
    
    // Clear any existing timer
    if (refreshTimer) {
      clearInterval(refreshTimer);
    }

    // Get current token
    const currentToken = localStorage.getItem('authToken');
    if (!currentToken) return;

    // Get token expiration time
    const expiration = getTokenExpiration(currentToken);
    if (!expiration) {
      // If can't determine expiration, use default interval
      const timer = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL);
      setRefreshTimer(timer);
      return;
    }

    // Calculate time until refresh (expiration minus buffer)
    const timeUntilRefresh = Math.max(0, expiration - Date.now() - TOKEN_EXPIRY_BUFFER);

    console.log(`Setting up token refresh in ${Math.floor(timeUntilRefresh / 1000)} seconds`);

    // Set one-time timeout for first refresh
    const initialTimer = setTimeout(async () => {
      const success = await refreshToken();

      if (success) {
        // After first refresh, set up regular interval (but use a longer interval to avoid rate limiting)
        // Increased to 30 minutes to prevent rate limiting from too many requests
        const intervalTimer = setInterval(refreshToken, 30 * 60 * 1000);
        setRefreshTimer(intervalTimer);
      }
    }, timeUntilRefresh);

    setRefreshTimer(initialTimer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track last verification time to avoid too many requests
  const [lastVerified, setLastVerified] = useState<number>(0);
  const lastVerifiedRef = useRef<number>(0); // Use ref to avoid triggering re-renders
  const MIN_VERIFICATION_INTERVAL = 60 * 1000; // Wait at least 1 minute between verifications

  // Update ref whenever state changes
  useEffect(() => {
    lastVerifiedRef.current = lastVerified;
  }, [lastVerified]);

  // Check for existing user session on initial load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if this is a Netlify deployment
        const isNetlifyDeployment = 
          window.location.hostname.includes('netlify.app') || 
          window.location.hostname.includes('github.io') ||
          window.location.hostname.includes('reporting.roiconstruction.com') ||
          process.env.REACT_APP_USE_MOCK_DATA === 'true' ||
          process.env.REACT_APP_NETLIFY === 'true';
        
        // For Netlify deployments, create a mock session if one doesn't exist
        if (isNetlifyDeployment) {
          console.log('Netlify deployment detected - using auto-authentication');
          
          // If there's already a mock session, use it
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            try {
              setUser(JSON.parse(storedUser));
              setToken('mock-auth-token-for-netlify');
              setLoading(false);
              return;
            } catch (e) {
              console.warn('Failed to parse stored user data');
            }
          }
          
          // Otherwise create a new mock session
          const mockUser = {
            id: '3',
            firstName: 'Demo',
            lastName: 'User',
            email: 'dev@roiexteriors.com',
            role: 'admin'
          };
          
          localStorage.setItem('authToken', 'mock-auth-token-for-netlify');
          localStorage.setItem('user', JSON.stringify(mockUser));
          setUser(mockUser);
          setToken('mock-auth-token-for-netlify');
          setLoading(false);
          return;
        }
        
        // Normal authentication flow for non-Netlify deployments
        const storedToken = localStorage.getItem('authToken');
        if (storedToken) {
          setToken(storedToken);

          // Get user from localStorage if available
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            try {
              setUser(JSON.parse(storedUser));
            } catch (e) {
              console.warn('Failed to parse stored user data');
            }
          }

          // Check if token is about to expire
          if (isTokenExpiring(storedToken)) {
            console.log('Token is expiring soon, refreshing...');
            const refreshed = await refreshToken();

            if (!refreshed) {
              // If refresh fails, try to use the existing token anyway
              console.warn('Token refresh failed, attempting to verify existing token');
            }
          }

          // Only verify with backend if it's been at least MIN_VERIFICATION_INTERVAL since last verification
          const now = Date.now();
          if (now - lastVerifiedRef.current > MIN_VERIFICATION_INTERVAL) {
            try {
              console.log('Attempting to verify auth token with backend');
              // Verify token with backend
              const response = await apiService.verifyAuth();
              
              // Check if response has user data
              if (!response.user) {
                throw new Error('No user data in verification response');
              }
              
              // Process user data and add role field
              const roles = response.roles || (response.user && response.user.roles && response.user.roles.map((r: any) => r.name)) || [];
              
              // Remove roles from user object to avoid confusion and add role string
              const { roles: userRoles, ...userWithoutRoles } = response.user;
              const userData = {
                ...userWithoutRoles,
                role: roles.length > 0 ? roles[0] : 'user'
              };
              
              setUser(userData);
              // Save user data to localStorage
              localStorage.setItem('user', JSON.stringify(userData));
              console.log('Successfully verified token with backend');
              setLastVerified(now);
              lastVerifiedRef.current = now; // Update ref directly for immediate effect
            } catch (err) {
              console.error('Token verification error:', err);
              // If token verification fails but we already have user data, continue session
              if (storedUser) {
                console.log('Using cached user data despite verification failure');
              } else {
                // Only clear everything if we don't have cached user data
                localStorage.removeItem('authToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('user');
                console.warn('Token verification failed - user will need to log in again');
              }
            }
          } else {
            console.log('Skipping verification, last verified', (now - lastVerifiedRef.current) / 1000, 'seconds ago');
          }

          // Set up refresh timer regardless of verification
          setupRefreshTimer();
        } else {
          console.log('No auth token found in local storage');
        }
      } catch (err) {
        console.error('Auth check error:', err);
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();

    // Cleanup function to clear refresh timer
    return () => {
      if (refreshTimer) {
        clearInterval(refreshTimer);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on component mount

  // Function to set loading state
  const setAuthLoading = (isLoading: boolean) => {
    setLoading(isLoading);
  };

  // Login function
  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setError(null);
      setLoading(true);

      // Use the actual provided credentials
      const loginEmail = email;
      const loginPassword = password;

      console.log('Attempting to log in with provided credentials:', loginEmail);
      console.log('Connecting to backend at:', getAuthUrlForDisplay());

      try {
        console.log('Connecting to backend for authentication...');
        const response = await apiService.login(loginEmail, loginPassword);

        // Validate the response structure
        if (!response) {
          console.error('Empty login response received');
          setError('Login failed: Empty response from server');
          return false;
        }

        // Check that expected data is present
        if (!response.user) {
          console.error('Login response missing user data:', response);
          setError('Login response missing user data');
          return false;
        }

        if (!response.accessToken && !response.access_token) {
          console.error('Login response missing token:', response);
          setError('Login response missing authentication token');
          return false;
        }

        console.log('Login successful, user:', response.user.email);
        console.log('Login response structure:', response);

        // Process user data and add role field
        // The roles can come from either response.roles or response.user.roles
        const roles = response.roles || (response.user.roles && response.user.roles.map((r: any) => r.name)) || [];
        console.log('Processed roles:', roles);
        
        // Remove roles from user object to avoid confusion and add role string
        const { roles: userRoles, ...userWithoutRoles } = response.user;
        const userData = {
          ...userWithoutRoles,
          role: roles.length > 0 ? roles[0] : 'user'
        };
        console.log('Final user data with role:', userData);

        // Update auth context state
        setUser(userData);
        setToken(response.accessToken || response.access_token);

        // Save user to localStorage for persistence
        localStorage.setItem('user', JSON.stringify(userData));

        // Update last verification time
        const loginNow = Date.now();
        setLastVerified(loginNow);
        lastVerifiedRef.current = loginNow; // Update ref directly

        // Set up token refresh timer
        setupRefreshTimer();

        return true;
      } catch (err: any) {
        console.error('Login error details:', {
          message: err.message,
          status: err.status,
          name: err.name,
          stack: err.stack
        });

        // More specific error handling with better messages
        if (err.message?.includes('Failed to fetch') || err.message?.includes('Network Error')) {
          setError('Unable to connect to authentication server. Please verify the backend server is running at http://localhost:3001');
        } else if (err.message?.includes('Invalid email or password')) {
          setError('Invalid credentials. Please check your email and password.');
        } else if (err.message?.includes('Too many login attempts')) {
          setError('Too many login attempts. Please try again later.');
        } else if (err.message?.includes('Server error')) {
          setError('Authentication server error. Please try again later or contact support.');
        } else if (err.message?.includes('Invalid response format')) {
          setError('Authentication response format error. The server response was invalid.');
        } else {
          setError(err.message || 'Authentication failed. Please try again.');
        }

        // Log more details for debugging in development
        if (process.env.NODE_ENV === 'development') {
          console.debug('Auth server connection details:', {
            url: `${API_BASE_URL}/auth/login`,
            error: err
          });
        }

        return false;
      } finally {
        setLoading(false);
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'An error occurred during login');
      setLoading(false);
      return false;
    }
  };

  // Helper function to display auth URL for debugging
  const getAuthUrlForDisplay = () => {
    // With proxy configuration, we're calling the backend through the proxy
    return 'http://localhost:3001/api/auth/login (via proxy)';
  };

  // Register function
  const register = async (
    firstName: string,
    lastName: string,
    email: string,
    password: string
  ): Promise<boolean> => {
    try {
      setError(null);
      setLoading(true);
      console.log('Attempting to register new user:', email);

      try {
        console.log('Connecting to backend for registration...');
        const response = await apiService.register(firstName, lastName, email, password);
        console.log('Registration successful, user:', response.user.email);

        // Process user data and add role field  
        const roles = response.roles || (response.user.roles && response.user.roles.map((r: any) => r.name)) || [];
        
        // Remove roles from user object to avoid confusion and add role string
        const { roles: userRoles, ...userWithoutRoles } = response.user;
        const userData = {
          ...userWithoutRoles,
          role: roles.length > 0 ? roles[0] : 'user'
        };

        // Update auth context state
        setUser(userData);
        setToken(response.accessToken || response.access_token);

        // Save user to localStorage for persistence
        localStorage.setItem('user', JSON.stringify(userData));

        // Update last verification time
        const loginNow = Date.now();
        setLastVerified(loginNow);
        lastVerifiedRef.current = loginNow; // Update ref directly

        // Set up token refresh timer
        setupRefreshTimer();

        return true;
      } catch (err: any) {
        console.error('Registration error:', err);

        // Provide a clear error message about API connection issues
        if (err.message?.includes('Network Error') || err.message?.includes('Failed to fetch')) {
          setError('Unable to connect to the registration server. Please check if the backend is running.');
        } else {
          setError(err.message || 'Registration failed');
        }

        return false;
      } finally {
        setLoading(false);
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.message || 'An error occurred during registration');
      setLoading(false);
      return false;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Call backend logout endpoint to invalidate the token
      await apiService.logout();
    } catch (err) {
      console.error('Error during logout:', err);
      // Continue with client-side logout even if server logout fails
    } finally {
      // Clear any refresh timer
      if (refreshTimer) {
        clearInterval(refreshTimer);
        setRefreshTimer(null);
      }

      // Clear local storage
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');

      // Update state
      setUser(null);
      setToken(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!user,
        loading,
        login,
        register,
        logout,
        refreshToken,
        setAuthLoading,
        error
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

export default AuthContext;